
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from "@/hooks/use-toast";
import { Waves } from 'lucide-react';

const ContactForm = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    shop: '',
    email: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('https://discord.com/api/webhooks/1364431715434758174/_muHTZUKFzoCz3HdUVSm95ZwTYN-I35SBYPzORmdN46sVfZQjpd119M6_jgB3ZJrQEZn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: `📬 New PayMe App Request\nName: ${formData.name}\nShop: ${formData.shop}\nEmail: ${formData.email}\nMessage: ${formData.message}`
        }),
      });
      
      if (response.ok) {
        toast({
          title: "Message Sent!",
          description: "We've received your request and will be in touch soon.",
        });
        setFormData({
          name: '',
          shop: '',
          email: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-ocean-200 to-ocean-300 bg-waves relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-ocean-800 mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-ocean-700 max-w-2xl mx-auto">
            Request early access or ask us anything about the Dive Shop Quote Generator
          </p>
        </div>
        
        <div className="max-w-xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white/90 backdrop-blur-sm shadow-xl rounded-lg p-8">
            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-ocean-700 font-medium mb-1">Name *</label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border-ocean-200 focus:border-ocean-500 focus:ring-ocean-500"
                />
              </div>
              
              <div>
                <label htmlFor="shop" className="block text-ocean-700 font-medium mb-1">Dive Shop Name</label>
                <Input
                  id="shop"
                  name="shop"
                  placeholder="Your dive shop name (if applicable)"
                  value={formData.shop}
                  onChange={handleChange}
                  className="w-full border-ocean-200 focus:border-ocean-500 focus:ring-ocean-500"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-ocean-700 font-medium mb-1">Email *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Your email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border-ocean-200 focus:border-ocean-500 focus:ring-ocean-500"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-ocean-700 font-medium mb-1">Message *</label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us what you're interested in"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full border-ocean-200 focus:border-ocean-500 focus:ring-ocean-500"
                  rows={4}
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full bg-ocean-600 hover:bg-ocean-700 py-6 text-lg shadow-blue"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </div>
                ) : (
                  <span className="flex items-center justify-center">
                    <Waves className="mr-2" size={20} />
                    Send Message
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
